// ─── Photo Row Layout (Phase 1.9) ─────────────────────────────
// Distributes 2-3 selected image elements as a horizontally aligned row
// centered on the canvas. The row occupies `ROW_WIDTH_PERCENT` (76%) of the
// canvas width with `GAP_PERCENT` (2%) gaps so the layout never touches the
// mobile edges. Each photo remains individually editable afterwards — no
// permanent group is created.
//
// Per-photo defaults applied:
//   role: 'photo', fit: 'cover', position: 'center', aspectRatio: '1:1',
//   anchor: 'c'.

import { IMAGE_ROLES } from './imageRoles';

export const PHOTO_ROW_WIDTH_PERCENT = 76;
export const PHOTO_ROW_GAP_PERCENT = 2;
export const PHOTO_ROW_MIN = 2;
export const PHOTO_ROW_MAX = 3;
export const PHOTO_ROW_X_CENTER = 50;

/**
 * Compute per-photo overrides for a centered photo row.
 *
 * @param {Array} elements - elements in the user-selected order
 * @returns {Array<{id: string, layout: object, role: string, customStyle: object}>}
 */
export function computePhotoRowOverrides(elements) {
  const n = elements.length;
  if (n < PHOTO_ROW_MIN || n > PHOTO_ROW_MAX) return [];

  const rowW = PHOTO_ROW_WIDTH_PERCENT;
  const gap = PHOTO_ROW_GAP_PERCENT;
  const perW = (rowW - (n - 1) * gap) / n;
  const leftEdgePercent = PHOTO_ROW_X_CENTER - rowW / 2; // = 12

  // Average Y across the selected photos (fall back to 50% if none have y).
  const ys = elements
    .map((el) => {
      const m = el.layout?.mobile || {};
      if (typeof m.yPercent === 'number') return m.yPercent;
      if (typeof m.y === 'number') return m.y * 100;
      return null;
    })
    .filter((v) => v != null);
  const avgY = ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : 50;

  return elements.map((el, i) => {
    const xCenter = leftEdgePercent + i * (perW + gap) + perW / 2;
    const mobile = el.layout?.mobile || {};
    const desktop = el.layout?.desktop || {};
    return {
      id: el.id,
      role: IMAGE_ROLES.PHOTO,
      customStyle: {
        ...(el.customStyle || {}),
        fit: 'cover',
        position: 'center',
        aspectRatio: '1:1',
      },
      layout: {
        ...(el.layout || {}),
        mobile: {
          ...mobile,
          xPercent: round2(xCenter),
          yPercent: round2(avgY),
          widthPercent: round2(perW),
          anchor: 'c',
          // Keep legacy x/y/w in sync for the save adapter.
          x: round4(xCenter / 100),
          y: round4(avgY / 100),
          w: round4(perW / 100),
        },
        desktop: {
          ...desktop,
          xPercent: round2(xCenter),
          yPercent: round2(avgY),
          widthPercent: round2(perW),
          anchor: 'c',
          x: round4(xCenter / 100),
          y: round4(avgY / 100),
          w: round4(perW / 100),
        },
      },
    };
  });
}

function round2(n) { return Math.round(n * 100) / 100; }
function round4(n) { return Math.round(n * 10000) / 10000; }
